import { IVec3, Vec3 } from "@base/math/Vec3";
import { ImmHelper } from "@base/util/Immutable";
import { Debug } from "@biz/Debug";
import { IMaterialSettings, ITexInfo } from "@biz/definitions/SunSailDefinitions";
import { GroundData } from "@biz/items/ground/GroundData";
import { GroundShape, IGroundData } from "@biz/items/ground/GroundDefinitions";
import { getItemLayerReducers } from "@biz/reducers/ItemLayerReducer";

export const GroundReducer = {
  ...getItemLayerReducers<IGroundData>(),
  setLength(old: IGroundData, l: number, originPoint: number | "center" = "center"): IGroundData {
    if (old.shape !== "rectangle")
      return old;

    const aspect = GroundData.getAspect(old);
    const b = aspect ? l / aspect : Vec3.len(old.corners[0], old.corners[old.corners.length - 1]);

    return {
      ...old,
      corners: GroundData.withSize(old.corners, l, b, originPoint)
    }
  },
  setWidth(old: IGroundData, b: number, originPoint: number | "center" = "center"): IGroundData {
    if (old.shape !== "rectangle")
      return old;

    const ab = Vec3.to(old.corners[0], old.corners[1])

    const aspect = GroundData.getAspect(old);
    const l = aspect ? b * aspect : Vec3.len(ab);

    return {
      ...old,
      corners: GroundData.withSize(old.corners, l, b, originPoint)
    }
  },
  setNiveau(old: IGroundData, niveau: number): IGroundData {
    return {
      ...old,
      corners: old.corners.map((c) => ImmHelper.setProperty(c, "y", niveau))
    }
  },
  setHeight(old: IGroundData, v: number): IGroundData {
    return { ...old, height: v }
  },
  setRound(old: IGroundData, round: boolean): IGroundData {
    return { ...old, round }
  },
  setShape(old: IGroundData, shape: GroundShape): IGroundData {
    if (old.shape === shape)
      return old;
    if (shape === "rectangle") {
      const l = Vec3.len(old.corners[0], old.corners[1])
      const aspect = GroundData.getAspect(old);
      const b = aspect ? l / aspect : Vec3.len(old.corners[1], old.corners[0])

      return {
        ...old,
        shape,
        corners: GroundData.withSize(old.corners, l, b, 0)
      }
    }
    return { ...old, shape }
  },
  setCorner(old: IGroundData, index: number, p: IVec3): IGroundData {
    if (old.shape === "poly") {
      return {
        ...old,
        corners: ImmHelper.setArrayItem(old.corners, index, p)
      }
    } else if (old.shape === "rectangle") {
      Debug.assert(old.corners.length === 4, "Invalid point count!")
      const op = (index + 2) % 4;
      const origin = old.corners[op];
      const vab = Vec3.to(origin, old.corners[(op + 1) % 4])
      const vac = Vec3.to(origin, old.corners[(op + 3) % 4])
      const diag = Vec3.to(origin, p);
      let ab: IVec3;
      let ac: IVec3;
      if (Vec3.len2(vab) >= Vec3.len2(vac)) {
        ab = Vec3.normalized(vab);
        ac = Vec3.from(-ab.z, 0, ab.x);
      } else {
        ac = Vec3.normalized(vac);
        ab = Vec3.from(ac.z, 0, -ac.x);
      }

      // console.log("Dot: " + index + " " + Vec3.dot(diag, ab) + " " + Vec3.dot(diag, ac));
      const aspect = GroundData.getAspect(old);
      const dab = Math.max(Vec3.dot(diag, ab), 0.2);
      const dac = aspect ? (op % 2 === 0 ? dab / aspect : dab * aspect) : Math.max(Vec3.dot(diag, ac), 0.2);

      const nab = Vec3.addScaled(origin, ab, dab);
      const nac = Vec3.addScaled(origin, ac, dac);
      let corners = ImmHelper.setArrayItem(old.corners, index, Vec3.addScaled(nab, ac, dac));
      corners = ImmHelper.setArrayItem(corners, (op + 1) % 4, nab)
      corners = ImmHelper.setArrayItem(corners, (op + 3) % 4, nac)

      return {
        ...old,
        corners
      }
    }
    return old;
  },
  setCorners(old: IGroundData, points: IVec3[]): IGroundData {
    return {
      ...old,
      corners: points
    }
  },
  addCorner(old: IGroundData, index: number, p?: IVec3): IGroundData {
    console.log("AddCorner: ", index, old);
    if (!p)
      p = Vec3.mid(old.corners[index], old.corners[(index + 1) % old.corners.length]);
    return {
      ...old,
      corners: ImmHelper.insertArrayItem(old.corners, index + 1, p)
    }
  },
  removeCorner(old: IGroundData, index: number, _p?: IVec3): IGroundData {
    const result = {
      ...old,
      corners: ImmHelper.removeArrayItem(old.corners, index),
    }
    return result;
  },
  setMaterial(old: IGroundData, material: IMaterialSettings): IGroundData {
    return ImmHelper.setProperty(old, "material", material);
  },
  // _setTextureImage(old: IGroundData, imageSource: ImageSource, normalSource?: ImageSource): IGroundData {
  //   const texInfo = TexInfoReducer.setTextureImage(old.texInfo, imageSource, normalSource);
  //   return GroundReducer._setTexInfo()
  //   const newGround = {
  //     ...old,
  //     texInfo
  //   }
  //   return GroundData.ensureAspect(newGround);
  // },
  setTexInfo(old: IGroundData, texInfo: ITexInfo): IGroundData {
    const newGround = {
      ...old,
      texInfo
    }
    return GroundData.ensureAspect(newGround);
  },
  setIgnoreAspect(old: IGroundData, ignoreAspect: boolean): IGroundData {
    const newGround = ImmHelper.setProperty(old, "ignoreAspect", ignoreAspect);
    return GroundData.ensureAspect(newGround);
  }
}